'use client';

import { defaultLanguage } from '@stereoagency/constants';
import {
  ArticleHero,
  Breadcrumbs,
  Container,
  ThumbnailListEntriesSection
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import flush from 'just-flush';
import { ReactNode, useContext } from 'react';
import {
  researchProjectPageLinkedResearchContentQuery,
  researchProjectPageQuery
} from './researchProjectPageQuery';
import { createDocumentURL, findInKeyArray, formatProfileName } from '@/utils';
import { SimpleBlockContent } from '@/components/sanity/client';
import { SpecialPagesContext } from '@/components/context';

type Props = {
  lang: string;
  page: (typeof researchProjectPageQuery)[1];
  linkedResearchContent?: (typeof researchProjectPageLinkedResearchContentQuery)[1]['linkedResearchContent'];
  content?: ReactNode;
  taxonomyCounts?: Record<string, number>;
};

export default function ResearchProjectPage({
  lang,
  page,
  linkedResearchContent,
  content,
  taxonomyCounts
}: Props) {
  const { t } = useTranslation('news');
  const { pages } = useContext(SpecialPagesContext);

  const title = page.title
    ? findInKeyArray(page.title, [lang, defaultLanguage.id])
    : null;

  const groupOrPlatformTitle = linkedResearchContent?.title
    ? findInKeyArray(linkedResearchContent.title, [lang, defaultLanguage.id])
    : null;

  const listingPage = linkedResearchContent
    ? linkedResearchContent?._type === 'researchGroup'
      ? pages.research_group_listing_page
      : pages.research_platform_listing_page
    : null;

  const breadcrumbActions = flush([
    listingPage
      ? {
          href: listingPage.href,
          label: listingPage.title
        }
      : null,
    linkedResearchContent?.slug?.current
      ? {
          href: createDocumentURL(
            lang,
            'researchGroup',
            linkedResearchContent.slug.current
          ),
          label: groupOrPlatformTitle?.value ?? ''
        }
      : null,
    page.slug?.current
      ? {
          href: createDocumentURL(lang, 'researchProject', page.slug.current),
          label: title?.value ?? ''
        }
      : null
  ]);

  return (
    <>
      <Container>
        <Breadcrumbs actions={breadcrumbActions} />
      </Container>
      <ArticleHero
        alignment="center"
        title={title?.value ?? ''}
        sublabel={page.author ? formatProfileName(page.author) : undefined}
        actions={flush(
          page.taxonomies?.map((tax) => {
            const name = tax.name
              ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
              : null;

            if (
              !name?.value ||
              !tax.slug?.current ||
              !linkedResearchContent?.slug?.current
            ) {
              return null;
            }

            const url = createDocumentURL(
              lang,
              'researchGroup',
              linkedResearchContent?.slug?.current
            );
            const count = taxonomyCounts?.[tax.slug.current] ?? 0;

            return {
              label: name.value,
              href: url,
              suffix: `(${count})`
            };
          }) ?? []
        )}
        accentColor="--colors-red-700"
      />

      {content}

      {!!page.relatedResearchProjects?.length && (
        <ThumbnailListEntriesSection
          className="bg-red-700"
          title={t('news:related-news.title')}
          entries={flush(
            page.relatedResearchProjects.map((related) => {
              const relatedTitle = related.title
                ? findInKeyArray(related.title, [lang, defaultLanguage.id])
                : null;
              const intro = related.introduction
                ? findInKeyArray(related.introduction, [
                    lang,
                    defaultLanguage.id
                  ])
                : null;

              if (!related.slug?.current || !relatedTitle?.value) {
                return null;
              }

              return {
                variant: 'inverted' as const,
                accentColor: '--colors-red-700',
                title: relatedTitle.value,
                description: intro?.value ? (
                  <SimpleBlockContent value={intro.value} />
                ) : null,
                action: {
                  href: createDocumentURL(
                    lang,
                    'researchProject',
                    related.slug.current
                  )
                }
              };
            })
          )}
        />
      )}
    </>
  );
}
